import styled from 'styled-components'
import { useCallback, useContext, useEffect, useId, useMemo, useRef } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import FlightSearchAirportInputs from './FlightSearchAirportInputs'
import TextButton from 'LuxKit/Button/TextButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import FlightSearchDateSelect from './FlightSearchDateSelect'
import FlightSearchDateSelectSheet from './FlightSearchDateSelectSheet'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import {
  StandaloneFlightMenu,
  type FlightSearchWidgetInitialFocus,
} from 'contexts/Flights/FlightSearchWidget/types'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import { applyFlightSearchInitialFocus } from './applyFlightSearchInitialFocus'

const InputsWrapper = styled.form`
  position: relative;
  display: none;

  ${mediaQueryUp.tablet} {
    display: grid;
    gap: ${rem(8)};
    column-gap: ${rem(12)};
    grid-template-areas:
      "location-input location-input location-input"
      "date-input date-input actions";
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  ${mediaQueryUp.desktop} {
    column-gap: ${rem(8)};
    grid-template-areas: "location-input date-input traveller-input actions";
    grid-template-columns: 1fr minmax(${rem(230)}, ${rem(342)}) auto ${rem(112)};
  }

  @container trip-flight-drawer-search (max-width: ${rem(760)}) {
    grid-template-areas:
      "location-input location-input"
      "date-input actions";
    grid-template-columns: minmax(0, 1fr) ${rem(112)};
  }

  > .location-input-area {
    grid-area: location-input;
  }
  > .date-input-area {
    grid-area: date-input;
  }
  > .actions-area {
    grid-area: actions;
  }
  > .traveller-input {
    grid-area: traveller-input;
    min-width: ${rem(178)};
  }
`

const SearchButton = styled(TextButton)`
  height: auto;
`

interface Props {
  readOnlyAirports?: boolean
  groupAirportDropdowns?: boolean
  portaledDropdowns?: boolean
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  autoFocusArrival?: boolean
  initialFocus?: FlightSearchWidgetInitialFocus
}

function FlightSearchWidgetDesktopForm(props: Props) {
  const { onSubmit, readOnlyAirports, autoFocusArrival, groupAirportDropdowns, portaledDropdowns, initialFocus } = props
  const intl = useIntl()
  const hasAppliedInitialFocus = useRef(false)
  const departureAirportField = useRef<HTMLInputElement>(null)
  const arrivalAirportField = useRef<HTMLInputElement>(null)
  const departingDateField = useRef<HTMLButtonElement>(null)
  const returningDateField = useRef<HTMLButtonElement>(null)
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const formRef = useRef<HTMLFormElement>(null)
  const formId = useId()
  const [flightDeals] = useFlightDeals()

  const id = useMemo(() => {
    return state.flights[0].id
  }, [state.flights])

  useEffect(() => {
    const focusTarget = initialFocus?.field ?? (autoFocusArrival ? 'arrivalAirport' : undefined)
    if (!focusTarget || hasAppliedInitialFocus.current) {
      return
    }

    const timeoutId = setTimeout(() => {
      hasAppliedInitialFocus.current = true
      applyFlightSearchInitialFocus(
        { field: focusTarget, flightId: id },
        {
          departureAirport: () => departureAirportField.current?.focus(),
          arrivalAirport: () => arrivalAirportField.current?.focus(),
          departureDate: () => departingDateField.current?.click(),
          returnDate: () => returningDateField.current?.click(),
        },
      )
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [autoFocusArrival, id, initialFocus])

  const onDepartureChange = useCallback(() => {
    arrivalAirportField.current?.focus()
  }, [arrivalAirportField])

  const onArrivalChange = useCallback(() => {
    departingDateField.current?.click()
  }, [])

  const handleDateClose = useCallback(() => {
    dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU })
  }, [dispatch])

  return (
    <InputsWrapper id={formId} onSubmit={onSubmit} ref={formRef}>
      <FlightSearchAirportInputs
        id={id}
        className="location-input-area"
        readOnly={readOnlyAirports}
        onDepartureChange={onDepartureChange}
        onArrivalChange={onArrivalChange}
        departingFieldRef={departureAirportField}
        arrivalFieldRef={arrivalAirportField}
        groupAirportDropdowns={groupAirportDropdowns}
        portaledAirportDropdowns={portaledDropdowns}
      />

      <FlightSearchDateSelect
        id={id}
        className="date-input-area"
        departingFieldRef={departingDateField}
        arrivalFieldRef={returningDateField}
      />

      <FlightSearchDateSelectSheet
        id={id}
        anchorRef={formRef}
        formId={formId}
        portaled={portaledDropdowns}
        isOpen={state.activeMenu === StandaloneFlightMenu.date}
        onClose={handleDateClose}
        flightDeals={flightDeals}
      />

      <SearchButton
        className="actions-area"
        kind="primary"
        size="large"
        data-testid="airportSearchButton"
        type="submit"
      >
        {intl.formatMessage({
          id: '6D2lT3mm3F43mkH3nYrE',
          defaultMessage: 'Search',
          description: 'Search button text for flight search widget in the flights flow',
        })}
      </SearchButton>
    </InputsWrapper>
  )
}

export default FlightSearchWidgetDesktopForm
